import { createFileRoute, Link } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Card } from '@/components/ui/card';
import { BRAND_NAME } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { PAGE_TITLE } from '@/components/ui/typography';

const DELETION_EMAIL = 'info@coretas.ai';

const searchSchema = z.object({
  code: z.string().optional(),
});

export const Route = createFileRoute('/data-deletion')({
  validateSearch: searchSchema,
  staticData: { title: 'route.data_deletion.title' },
  component: DataDeletionPage,
});

function DataDeletionPage() {
  const { code } = Route.useSearch();
  const { t } = useTranslation('nav');

  return (
    <main className="bg-bg-page flex min-h-screen items-center justify-center p-6">
      <Card className="border-border-1 flex w-full max-w-md flex-col gap-6 p-8">
        <img src="/images/logo.svg" alt={BRAND_NAME} className="h-9 w-auto" />

        <div className="flex flex-col gap-3">
          <h1 className={PAGE_TITLE}>{t('data_deletion.heading')}</h1>
          <p className="text-fg-3 text-fs-14 leading-relaxed">
            {t('data_deletion.deleted')}
          </p>
          <p className="text-fg-3 text-fs-14 leading-relaxed">
            <Trans
              i18nKey="data_deletion.self_service"
              ns="nav"
              values={{ email: DELETION_EMAIL }}
              components={{
                settings: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans fills this from the translated string
                  <a
                    href="https://www.facebook.com/settings?tab=applications"
                    target="_blank"
                    rel="noreferrer"
                    className="text-fg-1 underline"
                  />
                ),
                email: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans fills this from the translated string
                  <a
                    href={`mailto:${DELETION_EMAIL}`}
                    className="text-fg-1 underline"
                  />
                ),
              }}
            />
          </p>
        </div>

        {code && (
          <div className="border-border-1 flex flex-col gap-1 border-t pt-4">
            <span className="text-fg-3 text-fs-13">
              {t('data_deletion.reference_code')}
            </span>
            <code className="text-fg-1 text-fs-13 font-mono break-all">
              {code}
            </code>
          </div>
        )}

        <footer className="border-border-1 text-fs-13 flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-4">
          <a
            href="https://www.coretas.ai/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-fg-3 hover:text-fg-1 underline-offset-4 hover:underline"
          >
            {t('data_deletion.privacy_policy')}
          </a>
          <a
            href="https://www.coretas.ai/terms-of-use"
            target="_blank"
            rel="noreferrer"
            className="text-fg-3 hover:text-fg-1 underline-offset-4 hover:underline"
          >
            {t('data_deletion.terms_of_use')}
          </a>
          <Link
            to={ROUTES.HOME}
            className="text-fg-3 hover:text-fg-1 underline-offset-4 hover:underline"
          >
            {t('data_deletion.home')}
          </Link>
        </footer>
      </Card>
    </main>
  );
}
